import { defineStore } from 'pinia'
import type { ISelectItem } from '@/components/ui/select/types'
import type { ICountry, ICountryByLanguage, ICountriesResponse } from '@/stores/types'
import { COUNTRIES_LIST, BALTIC_COUNTRY_LIST, INTERNAL_LANGUAGES, OTHER_COUNTRY } from '@/utils/constants/locale'
import { useLocaleStore } from '@/stores/Locale.store'
import { useAirbalticApi } from '@/services/useAirbalticApi'
import { useDynamicTranslation } from '@/composables/useDynamicTranslation'
import { sortByLocaleCompare } from '@/utils/localeCompare'

export const useCountriesStore = defineStore('countriesStore', () => {
  const localeStore = useLocaleStore()

  const { translate } = useDynamicTranslation()

  const countries = ref<ICountry>({})
  const cachedCountries = ref<ICountryByLanguage>({})

  watch(
    () => localeStore.selectedLanguage,
    () => {
      if (INTERNAL_LANGUAGES.includes(localeStore.selectedLanguage)) {
        getCountriesFromAPI()
      }
    }
  )

  const setCountries = (value: ICountry) => {
    if (!value) return

    countries.value = value
    cachedCountries.value[localeStore.selectedLanguage] = value
  }

  const availableCountries = computed(() => {
    let countriesData: ISelectItem[] = []

    const translatedOtherCountriesLabel = translate('country.other')

    const otherCountry = OTHER_COUNTRY(translatedOtherCountriesLabel)

    if (Object.keys(countries.value).length === 0) {
      return [otherCountry]
    }

    const countryData: ISelectItem[] = COUNTRIES_LIST.filter((id: string) => !!countries.value[id]).map(
      (id: string) => ({
        id: countries.value[id].code,
        label: countries.value[id].name,
        image: countries.value[id].flagPng
      })
    )
    const balticData = countryData.splice(0, BALTIC_COUNTRY_LIST.length)

    sortByLocaleCompare(countryData, 'label')

    countriesData = [...balticData, ...countryData, otherCountry]

    const isCountryAvailable = countriesData.find((item: ISelectItem) => item.id === localeStore.selectedCountry)

    if (!isCountryAvailable) {
      localeStore.setSelectedCountry(countriesData[0].id)
    }

    return countriesData
  })

  const getCountriesFromAPI = async () => {
    const { api } = useAirbalticApi()
    const cached = cachedCountries.value[localeStore.selectedLanguage]

    if (cached) {
      setCountries(cached)

      return
    }

    try {
      const response = await api<ICountriesResponse>(
        `/data/storyblok/countries/${localeStore.selectedLanguage}/data.json`
      )

      setCountries(response._children)
    } catch (error) {
      console.error('Failed to fetch data:', error)
    }
  }

  return {
    countries,
    availableCountries,
    getCountriesFromAPI,
    cachedCountries
  }
})
